import { useApiTestingLanguage } from './useApiTestingLanguage';
import React from 'react';
import { Button } from '../ui/button';
import { Plus, Trash2, Target, Download } from 'lucide-react';

export const AssertionsTabContent = ({ testForm, setTestForm }) => {
  const { t } = useApiTestingLanguage();
  const addAssertion = (type = 'status_code', operator = 'equals', expected_value = '200', description = '') => {
    setTestForm({
      ...testForm,
      assertions: [...(testForm.assertions || []), { type, operator, expected_value, description }]
    });
  };

  const updateAssertion = (index, field, value) => {
    const newAssertions = [...testForm.assertions];
    newAssertions[index][field] = value;
    setTestForm({ ...testForm, assertions: newAssertions });
  };

  const removeAssertion = (index) => {
    const newAssertions = testForm.assertions.filter((_, i) => i !== index);
    setTestForm({ ...testForm, assertions: newAssertions });
  };

  return (
    <div className="bg-gray-50 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-gray-700">{t("Response Assertions")}</h3>
          <p className="text-xs text-gray-500">{t("Define conditions to validate the API response")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addAssertion()}
          data-testid="btn-add-assertion"
        >
          <Plus className="h-3 w-3 mr-1" />
          {t("Add Assertion")}
        </Button>
      </div>

      {(!testForm.assertions || testForm.assertions.length === 0) ? (
        <div className="text-center py-8">
          <Target className="h-8 w-8 mx-auto text-gray-400 mb-3" />
          <p className="text-sm text-gray-500 mb-4">
            {t("No assertions configured. Add assertions to validate response status, body content, or headers.")}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => addAssertion('status_code', 'equals', '200', t("Status is 200 OK"))}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
            >
              {t("+ Status Code = 200")}
            </button>
            <button
              type="button"
              onClick={() => addAssertion('response_time', 'less_than', '1000', t("Response under 1s"))}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
            >
              {t("+ Response Time < 1s")}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {testForm.assertions.map((assertion, index) => (
            <div key={index} className="bg-white rounded-lg border p-4 space-y-3" data-testid={`assertion-row-${index}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">{t("Type")}</label>
                  <select
                    value={assertion.type}
                    onChange={(e) => updateAssertion(index, 'type', e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="status_code">{t("Status Code")}</option>
                    <option value="response_time">{t("Response Time (ms)")}</option>
                    <option value="body_contains">{t("Body Contains")}</option>
                    <option value="body_json_path">{t("JSON Path")}</option>
                    <option value="header_contains">{t("Header Contains")}</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">{t("Operator")}</label>
                  <select
                    value={assertion.operator}
                    onChange={(e) => updateAssertion(index, 'operator', e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="equals">{t("Equals")}</option>
                    <option value="not_equals">{t("Not Equals")}</option>
                    <option value="contains">{t("Contains")}</option>
                    <option value="less_than">{t("Less Than")}</option>
                    <option value="greater_than">{t("Greater Than")}</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">{t("Expected Value")}</label>
                  <input
                    type="text"
                    value={assertion.expected_value}
                    onChange={(e) => updateAssertion(index, 'expected_value', e.target.value)}
                    placeholder="200"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeAssertion(index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 block mb-1">{t("Description (optional)")}</label>
                <input
                  type="text"
                  value={assertion.description || ''}
                  onChange={(e) => updateAssertion(index, 'description', e.target.value)}
                  placeholder={t("e.g., Verify successful response")}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Extractions Tab - Variable extraction for data chaining
 */
export const ExtractionsTabContent = ({ testForm, setTestForm }) => {
  const { t } = useApiTestingLanguage();
  const addExtraction = (variable_name = '', source = 'body', path = '', fallback = '', description = '') => {
    setTestForm({
      ...testForm,
      extractions: [...(testForm.extractions || []), { variable_name, source, path, fallback, description }]
    });
  };

  const updateExtraction = (index, field, value) => {
    const newExtractions = [...testForm.extractions];
    if (field === 'variable_name') {
      // Sanitize variable name
      value = value.replace(/[^a-zA-Z0-9_]/g, '');
    }
    if (field === 'source' && (value === 'status_code' || value === 'response_time')) {
      // Clear path when switching to status_code or response_time
      newExtractions[index].path = '';
    }
    newExtractions[index][field] = value;
    setTestForm({ ...testForm, extractions: newExtractions });
  };

  const removeExtraction = (index) => {
    const newExtractions = testForm.extractions.filter((_, i) => i !== index);
    setTestForm({ ...testForm, extractions: newExtractions });
  };

  return (
    <div className="bg-gray-50 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-medium text-gray-700">{t("Variable Extractions")}</h3>
          <p className="text-xs text-gray-500">{t("Extract values from responses to use in subsequent API requests (data chaining)")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addExtraction()}
          data-testid="btn-add-extraction"
        >
          <Plus className="h-3 w-3 mr-1" />
          {t("Add Extraction")}
        </Button>
      </div>

      {(!testForm.extractions || testForm.extractions.length === 0) ? (
        <div className="text-center py-8">
          <Download className="h-8 w-8 mx-auto text-gray-400 mb-3" />
          <p className="text-sm text-gray-500 mb-2">
            {t("No extractions configured.")}
          </p>
          <p className="text-xs text-gray-400 mb-4">
            {t("Extract values from this response to use in other API tests via")} {'{{variableName}}'} {t("syntax.")}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => addExtraction('authToken', 'body', '$.token', '', t("Extract auth token from response"))}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
            >
              {t("+ Extract Token ($.token)")}
            </button>
            <button
              type="button"
              onClick={() => addExtraction('userId', 'body', '$.user.id', '', t("Extract user ID from response"))}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
            >
              {t("+ Extract User ID ($.user.id)")}
            </button>
            <button
              type="button"
              onClick={() => addExtraction('statusCode', 'status_code', '', '', t("Capture response status code"))}
              className="px-3 py-1.5 text-sm bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
            >
              {t("+ Status Code")}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {testForm.extractions.map((extraction, index) => (
            <div key={index} className="bg-white rounded-lg border p-4 space-y-3" data-testid={`extraction-row-${index}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">{t("Variable Name")}</label>
                  <input
                    type="text"
                    value={extraction.variable_name}
                    onChange={(e) => updateExtraction(index, 'variable_name', e.target.value)}
                    placeholder="authToken"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">{t("Source")}</label>
                  <select
                    value={extraction.source}
                    onChange={(e) => updateExtraction(index, 'source', e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded-md"
                  >
                    <option value="body">{t("Response Body")}</option>
                    <option value="headers">{t("Response Headers")}</option>
                    <option value="status_code">{t("Status Code")}</option>
                    <option value="response_time">{t("Response Time (ms)")}</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">
                    {extraction.source === 'headers' ? t("Header Name") : t("Path (JSONPath/Dot)")}
                  </label>
                  <input
                    type="text"
                    value={extraction.path}
                    onChange={(e) => updateExtraction(index, 'path', e.target.value)}
                    placeholder={
                      extraction.source === 'headers'
                        ? 'Authorization'
                        : extraction.source === 'body'
                          ? t("$.data.token or user.id")
                          : ''
                    }
                    disabled={extraction.source === 'status_code' || extraction.source === 'response_time'}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md font-mono disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeExtraction(index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">{t("Fallback Value (optional)")}</label>
                  <input
                    type="text"
                    value={extraction.fallback || ''}
                    onChange={(e) => updateExtraction(index, 'fallback', e.target.value)}
                    placeholder={t("Default value if extraction fails")}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">{t("Description (optional)")}</label>
                  <input
                    type="text"
                    value={extraction.description || ''}
                    onChange={(e) => updateExtraction(index, 'description', e.target.value)}
                    placeholder={t("e.g., Extract auth token for subsequent requests")}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                {t("Use as:")} <code className="bg-blue-100 px-1 rounded">{`{{${extraction.variable_name || 'variableName'}}}`}</code> {t("in headers, body, or URL of other tests")}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Help section */}
      <div className="mt-4 pt-4 border-t">
        <p className="text-xs font-medium text-gray-600 mb-2">{t("Extraction Path Syntax:")}</p>
        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
          <div><code className="bg-gray-100 px-1 rounded">$.token</code> {t("- JSONPath: root level \"token\"")}</div>
          <div><code className="bg-gray-100 px-1 rounded">$.data.user.id</code> {t("- JSONPath: nested path")}</div>
          <div><code className="bg-gray-100 px-1 rounded">$.items[0].name</code> {t("- JSONPath: array access")}</div>
          <div><code className="bg-gray-100 px-1 rounded">user.profile.email</code> {t("- Dot notation")}</div>
        </div>
      </div>
    </div>
  );
};
